import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
} from "@mui/material";
import {
  Payment as PaymentIcon,
  Refresh as RetryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

interface PaymentInfo {
  amount: number;
  status: string;
  retryAttempt: number;
  originalPaymentId: string;
}

interface SubmissionInfo {
  id: number;
  uniqueId: string;
  form: {
    id: number;
    title: string;
    customUrl: string;
    settings: any;
  };
  paymentInfo: PaymentInfo;
  data: any;
  createdAt: string;
}

const RetryPaymentPage: React.FC = () => {
  const { uniqueId, paymentID } = useParams<{
    uniqueId: string;
    paymentID: string;
  }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [retryLoading, setRetryLoading] = useState(false);
  const [submission, setSubmission] = useState<SubmissionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  // Fetch submission and payment information
  useEffect(() => {
    const fetchRetryInfo = async () => {
      if (!uniqueId || !paymentID) {
        setError(
          "Invalid retry payment URL. Missing submission ID or payment ID."
        );
        setLoading(false);
        return;
      }

      try {
        console.log(
          `🔄 Fetching retry info for submission: ${uniqueId}, payment: ${paymentID}`
        );

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/bkash/retry-info/${uniqueId}/${paymentID}`
        );

        if (response.data.success) {
          if (response.data.alreadyCompleted) {
            setAlreadyCompleted(true);
            setSubmission(response.data.submission);
          } else {
            setSubmission(response.data.submission);
          }
        } else {
          setError(
            response.data.message || "Failed to load retry payment information"
          );
        }
      } catch (error: any) {
        console.error("Failed to fetch retry info:", error);
        setError(
          error.response?.data?.message ||
            "Failed to load payment information. Please check the URL and try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRetryInfo();
  }, [uniqueId, paymentID]);

  // Navigate back to form
  const handleBackToForm = () => {
    if (submission?.form?.customUrl) {
      navigate(`/${submission.form.customUrl}`);
    } else {
      navigate("/");
    }
  };

  // Navigate to success page if already completed
  const handleViewSubmission = () => {
    if (submission?.form?.customUrl) {
      navigate(
        `/success/${submission.form.customUrl}?submissionId=${submission.id}&uniqueId=${submission.uniqueId}`
      );
    } else {
      navigate(`/view-submission/${uniqueId}`);
    }
  };

  // Handle retry payment
  const handleRetryPayment = async () => {
    if (!uniqueId || !paymentID || !submission) {
      setError("Missing required information for retry payment");
      return;
    }
    if (submission.paymentInfo.status === "completed") {
      setError("Payment has already been completed");
      return;
    }

    setRetryLoading(true);
    setError(null);

    try {
      console.log(`🔄 Initiating retry payment for submission: ${uniqueId}`);

      // Create new payment for retry
      const retryResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/bkash/retry/${uniqueId}/${paymentID}`,
        {} // Empty body - amount will be taken from original submission
      );

      if (retryResponse.data.success) {
        const {
          paymentID: newPaymentID,
          bkashURL,
          retryAttempt,
        } = retryResponse.data;

        // Store retry payment session data
        sessionStorage.setItem(
          "bkashPaymentSession",
          JSON.stringify({
            paymentID: newPaymentID,
            submissionId: submission.id,
            uniqueId: submission.uniqueId,
            editCode: "", // Edit code not needed for retry
            amount: submission.paymentInfo.amount,
            timestamp: Date.now(),
            formData: submission.data,
            customUrl: submission.form.customUrl,
            isRetry: true,
            retryAttempt: retryAttempt,
            originalPaymentId: paymentID,
          })
        );

        console.log(
          `✅ Retry payment created: ${newPaymentID}, redirecting to bKash...`
        );

        // Redirect to bKash payment URL
        window.location.href = bkashURL;
      } else {
        setError(
          retryResponse.data.message || "Failed to create retry payment"
        );
      }
    } catch (error: any) {
      console.error("Retry payment error:", error);
      setError(
        error.response?.data?.message ||
          "Failed to initiate retry payment. Please try again."
      );
    } finally {
      setRetryLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          px={{ xs: 2, sm: 0 }}
        >
          <CircularProgress
            size={60}
            sx={{
              mb: 3,
              width: { xs: "50px !important", sm: "60px !important" },
              height: { xs: "50px !important", sm: "60px !important" },
            }}
          />
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: "1.125rem", sm: "1.25rem" } }}
          >
            Loading Payment Information...
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ textAlign: "center" }}
          >
            Please wait while we retrieve your payment details
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !submission) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          px={{ xs: 2, sm: 0 }}
        >
          <Paper
            elevation={3}
            sx={{ p: { xs: 3, sm: 4 }, textAlign: "center", width: "100%" }}
          >
            <ErrorIcon
              color="error"
              sx={{ fontSize: { xs: 48, sm: 60 }, mb: 2 }}
            />
            <Typography
              variant="h5"
              gutterBottom
              color="error"
              sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
            >
              Payment Information Not Found
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, wordBreak: "break-word" }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={handleViewSubmission}
              startIcon={<InfoIcon />}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Go to View Submission
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (alreadyCompleted && submission) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          px={{ xs: 2, sm: 0 }}
        >
          <Paper
            elevation={3}
            sx={{ p: { xs: 3, sm: 4 }, textAlign: "center", width: "100%" }}
          >
            <SuccessIcon
              color="success"
              sx={{ fontSize: { xs: 48, sm: 60 }, mb: 2 }}
            />
            <Typography
              variant="h5"
              gutterBottom
              color="success.main"
              sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
            >
              Payment Already Completed
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, px: { xs: 1, sm: 0 } }}>
              The payment for this submission has already been completed
              successfully. No retry is needed.
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 3, wordBreak: "break-word" }}
            >
              Form: {submission.form.title}
            </Typography>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={2}
              justifyContent="center"
              width="100%"
            >
              <Button
                variant="contained"
                onClick={handleViewSubmission}
                startIcon={<SuccessIcon />}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                View Submission
              </Button>
              {/* <Button
                variant="outlined"
                onClick={handleBackToForm}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Back to Form
              </Button> */}
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 3 } }}>
      <Box py={{ xs: 2, sm: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, overflow: "hidden" }}>
          <Box display="flex" alignItems="center" mb={3} flexWrap="wrap">
            <RetryIcon
              color="warning"
              sx={{ fontSize: { xs: 32, sm: 40 }, mr: 2 }}
            />
            <Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
              >
                Retry Payment
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Complete your payment to submit the form
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {submission && (
            <>
              {/* Submission Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📋 Submission Details
                  </Typography>
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: 2, sm: 3 }}
                    flexWrap="wrap"
                  >
                    <Box flex={1} minWidth={{ xs: "100%", sm: "200px" }}>
                      <Typography variant="body2" color="textSecondary">
                        Form Title
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {submission.form.title}
                      </Typography>
                    </Box>
                    <Box flex={1} minWidth={{ xs: "100%", sm: "200px" }}>
                      <Typography variant="body2" color="textSecondary">
                        Submission ID
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {submission.uniqueId}
                      </Typography>
                    </Box>
                    <Box flex={1} minWidth={{ xs: "100%", sm: "200px" }}>
                      <Typography variant="body2" color="textSecondary">
                        Submitted On
                      </Typography>
                      <Typography variant="body1">
                        {new Date(submission.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Payment Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    💳 Payment Information
                  </Typography>
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: 2, sm: 3 }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexWrap="wrap"
                  >
                    <Box flex={1} minWidth={{ xs: "100%", sm: "200px" }}>
                      <Typography variant="body2" color="textSecondary">
                        Payment Amount
                      </Typography>
                      <Typography
                        variant="h5"
                        color="primary.main"
                        fontWeight="bold"
                        sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
                      >
                        ৳{submission.paymentInfo.amount}
                      </Typography>
                    </Box>
                    <Box flex={1} minWidth={{ xs: "100%", sm: "200px" }}>
                      <Typography variant="body2" color="textSecondary">
                        Payment Status
                      </Typography>
                      <Chip
                        label={submission.paymentInfo.status.toUpperCase()}
                        color={
                          submission.paymentInfo.status === "completed"
                            ? "success"
                            : "error"
                        }
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    {submission.paymentInfo.retryAttempt > 0 && (
                      <Box width="100%">
                        <Typography variant="body2" color="textSecondary">
                          Previous Attempts:{" "}
                          {submission.paymentInfo.retryAttempt + 1}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Divider sx={{ my: 3 }} />

              {/* Action Buttons */}
              <Box textAlign="center">
                <Typography
                  variant="body1"
                  sx={{ mb: 3, px: { xs: 1, sm: 0 } }}
                >
                  Click the button below to retry your bKash payment:
                </Typography>

                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                  justifyContent="center"
                  alignItems="center"
                  px={{ xs: 1, sm: 0 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleRetryPayment}
                    disabled={retryLoading}
                    startIcon={
                      retryLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <PaymentIcon />
                      )
                    }
                    sx={{
                      minWidth: { xs: "100%", sm: "200px" },
                      maxWidth: { xs: "100%", sm: "300px" },
                      width: { xs: "100%", sm: "auto" },
                      py: 1.5,
                      background:
                        "linear-gradient(45deg, #E2136E 30%, #FF8E8E 90%)",
                      "&:hover": {
                        background:
                          "linear-gradient(45deg, #C11157 30%, #FF7777 90%)",
                      },
                    }}
                  >
                    {retryLoading
                      ? "Creating Payment..."
                      : "Retry bKash Payment"}
                  </Button>
                  {/* <Button
                    variant="outlined"
                    size="large"
                    onClick={handleBackToForm}
                    disabled={retryLoading}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 'auto' },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    Back to Form
                  </Button> */}
                </Box>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    mt: 2,
                    px: { xs: 1, sm: 0 },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  You will be redirected to bKash to complete the payment
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default RetryPaymentPage;
